import { useEffect, useRef, useState } from 'react';

interface CounterProps {
  value: number;
  prefix?: string;
  suffix?: string;
  label: string;
}

function AnimatedCounter({ value, prefix = '', suffix = '', label }: CounterProps) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1800;
          const startTime = Date.now();
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * value));
            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return (
    <div ref={ref} className="text-center min-w-0">
      <div
        className="text-2xl sm:text-3xl md:text-4xl font-semibold mb-1"
        style={{ fontFamily: 'var(--font-heading)', color: '#C8A96A' }}
      >
        {prefix}{count}{suffix}
      </div>
      <div
        className="text-[10px] sm:text-xs md:text-sm break-words"
        style={{
          fontFamily: 'var(--font-body)',
          color: 'rgba(248, 247, 245, 0.4)',
          fontWeight: 400,
        }}
      >
        {label}
      </div>
    </div>
  );
}

export default function About() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="sobre" className="relative py-20 md:py-28" style={{ backgroundColor: '#0E1412', overflowX: 'hidden' }}>
      {/* Subtle top gradient separator */}
      <div
        className="absolute top-0 left-0 right-0 h-[1px]"
        style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(200, 169, 106, 0.15) 50%, transparent 100%)' }}
      />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12 w-full">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 items-center w-full">
          {/* Photo */}
          <div
            className="flex-shrink-0 w-full lg:w-[400px] xl:w-[440px]"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0)' : 'translateX(-40px)',
              transition: 'all 1s ease-out',
            }}
          >
            <div className="relative w-full">
              <div
                className="absolute -top-8 -left-4 sm:-left-8 w-32 h-32 sm:w-48 sm:h-48 rounded-full"
                style={{
                  background: 'radial-gradient(circle, rgba(200, 169, 106, 0.1) 0%, transparent 70%)',
                  filter: 'blur(40px)',
                }}
              />
              <div className="relative overflow-hidden rounded-[24px] md:rounded-[28px] w-full"
                style={{
                  border: '1px solid rgba(200, 169, 106, 0.12)',
                  aspectRatio: '4 / 5',
                }}
              >
                <img
                  src="https://storage.readdy-site.link/project_files/a5718860-87a7-40cd-af11-814f205df039/de57f55e-bfee-4614-8629-6c4c93f0056b_compressed_Captura-de-tela-2026-07-24-220158.webp"
                  alt="Dra. Melissa Ciríaco"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div
            className="flex-1 min-w-0 w-full"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0)' : 'translateX(40px)',
              transition: 'all 1s ease-out 0.2s',
            }}
          >
            <p
              className="text-sm tracking-[0.15em] mb-4"
              style={{
                fontFamily: 'var(--font-body)',
                color: '#C8A96A',
                fontWeight: 500,
              }}
            >
              SOBRE
            </p>
            <h2
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-5 md:mb-6"
              style={{
                fontFamily: 'var(--font-heading)',
                color: '#F8F7F5',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              Dra. Melissa Ciríaco
              <br />
              <span style={{ color: 'rgba(248, 247, 245, 0.5)', fontWeight: 400 }}>
                CRM-CE 21438
              </span>
            </h2>
            <p
              className="text-sm md:text-base leading-relaxed mb-8"
              style={{
                fontFamily: 'var(--font-body)',
                color: 'rgba(248, 247, 245, 0.5)',
                fontWeight: 300,
                lineHeight: 1.9,
              }}
            >
              Dra. Melissa Ciríaco é médica pós-graduada em Nutrologia pela ABRAN, com atuação focada em medicina metabólica e hormonal. Especialista em emagrecimento saudável, reposição e modulação hormonal bioidêntica, tratamento do lipedema e otimização da saúde metabólica, acredita que cada paciente possui necessidades únicas e merece um plano terapêutico individualizado.
Sua abordagem vai além da perda de peso. Por meio de uma investigação criteriosa, busca compreender a origem dos desequilíbrios hormonais e metabólicos, promovendo tratamentos personalizados que restauram o equilíbrio do organismo, melhoram a composição corporal e proporcionam mais saúde, bem-estar e qualidade de vida.
            </p>

            {/* Animated counters - no mobile use smaller text */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-8 mb-10 p-3 sm:p-4 md:p-8 rounded-[16px] md:rounded-[20px] w-full"
              style={{
                backgroundColor: 'rgba(248, 247, 245, 0.02)',
                border: '1px solid rgba(248, 247, 245, 0.06)',
              }}
            >
              <AnimatedCounter value={21438} label="CRM-CE" />
              <AnimatedCounter value={100} prefix="+" label="Pacientes acompanhados" />
              <AnimatedCounter value={60} suffix="min" label="Consulta personalizada" />
              <AnimatedCounter value={100} suffix="%" label="Atendimento humanizado" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}